import { z } from 'zod'

import { calendarDateSchema, isoDateSchema } from '@/schemas/portfolio'
import { tableQuerySchema } from '@/schemas/table'

/**
 * Market · Events — the corporate calendar as a paged list.
 *
 * `dateLabel` is an AD wire date converted by eventsService to "BS (AD)" —
 * e.g. "7 Bhadra (23 Aug)", via `formatBsWithAd` — for the row's trailing
 * date. The raw wire date carries no separate BS field of its own; this
 * string is display-only, never re-parsed for sorting or query semantics —
 * `date` below is the field anything that compares or filters by day reads
 * instead.
 */

export const EVENT_KINDS = ['dividend', 'ipo', 'agm', 'rights', 'auction'] as const

export const eventKindSchema = z.enum(EVENT_KINDS)

export const eventRowSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  dateLabel: z.string().min(1),
  /** AD ISO `YYYY-MM-DD`, wire-verbatim — kept alongside the BS `dateLabel`
   *  so the date chip's "on or after" comparison has something sortable to
   *  read; see this file's header comment on why `dateLabel` itself is not it. */
  date: isoDateSchema,
  /** The exchange ticker (schema.d.ts `MarketEvent.company`). `null` for a
   *  notice with no single listed company behind it. */
  company: z.string().min(1).nullable(),
  /** Always present, unlike `company` — the search box and the "Any company"
   *  menu both key off this rather than the sometimes-null ticker. */
  companyName: z.string().min(1),
  /** Chooses the leading tile's label (BOOK/IPO/AGM/RIGHT/AUCTION) — see
   *  `KIND_LABEL` below. */
  kind: eventKindSchema,
})

export type EventKind = z.infer<typeof eventKindSchema>
export type EventRow = z.infer<typeof eventRowSchema>

/**
 * The kind tile's text (Title Case, as the mock sets it: "Book", "IPO", "AGM", "Right"), V2 session 5304 (`4236:111727`): a plain, uniformly
 * toned square per row, distinguished by its label alone — not a colour or
 * icon per kind. Confirmed for four of the five `EventKind`s on the rendered
 * frame; `auction` never appears in the sampled rows, so it keeps the same
 * word this app already uses for the kind elsewhere (PublicIssuesScreen,
 * portfolio's add-transaction kinds) rather than an invented abbreviation.
 *
 * Lives here rather than in `EventList` (a `.tsx` component file exports only
 * its component, for Fast Refresh — `empty-state-variants.ts` is the same
 * split for `EmptyState`). The Type menu's own wording is `KIND_FILTER_LABEL`.
 */
export const KIND_LABEL: Record<EventKind, string> = {
  dividend: 'Book',
  ipo: 'IPO',
  agm: 'AGM',
  rights: 'Right',
  auction: 'Auction',
}

/**
 * The kind as a word in a row's meta line (Home's events panel), where `KIND_LABEL` is the caps text
 * of Events' tile. One file holds both, so the two pages read the same five words: "Rights" where
 * the tile says "RIGHT". Only `dividend` and `ipo` differ from the tile's word, because the tile
 * names the book-closure date ("BOOK") and the meta line names what the notice is for.
 */
export const KIND_WORD: Record<EventKind, string> = {
  dividend: 'Dividend',
  ipo: 'Issue closes',
  agm: 'AGM',
  rights: 'Rights',
  auction: 'Auction',
}

/**
 * The Type menu's words (Title Case, as the mock's "Any type" menu sets them): the book-closure
 * date is what a `dividend` event is, and `auction` has no mock word, so it keeps the one the tile
 * and PublicIssuesScreen use. Apart from `KIND_LABEL` because the tile has 40px to say it in and
 * the menu does not.
 */
export const KIND_FILTER_LABEL: Record<EventKind, string> = {
  dividend: 'Book closure',
  ipo: 'IPO',
  agm: 'AGM',
  rights: 'Rights',
  auction: 'Auction',
}

/** `/market/events/`'s `q` is `max_length=64`; a longer one is a 400. */
export const EVENTS_SEARCH_MAX_LENGTH = 64

/**
 * The route's own query: `tableQuerySchema` plus every filter the screen offers. All of them are
 * `/api/v5/market/events/` params, so mm-be filters and pages the calendar itself — `type` is
 * `type`, `query` is `q` (symbol or name), `date` is `start` and `dateTo` is `end`.
 *
 * `.optional().catch(undefined)` on every field, the rule `PublicIssuesScreen` and `HeatmapScreen`
 * both state: a hand-edited or stale param falls back to "unfiltered" rather than throwing out of
 * `validateSearch` and replacing the whole screen with an error page over what is only ever a
 * filter, never a different screen.
 */
export const eventsQuerySchema = tableQuerySchema.extend({
  type: eventKindSchema.optional().catch(undefined),
  /** A symbol or a company name, whole or part — mm-be's `q`. */
  query: z.string().trim().min(1).optional().catch(undefined),
  /** AD ISO `YYYY-MM-DD`; events on or after this day. A day that doesn't exist falls back too. */
  date: calendarDateSchema.optional().catch(undefined),
  /** AD ISO; with `date`, the last day of a range (the picker's Date range tab). Alone it is ignored. */
  dateTo: calendarDateSchema.optional().catch(undefined),
})

export type EventsQuery = z.infer<typeof eventsQuerySchema>
